import {Injectable} from '@angular/core';
import {catchError, map, Observable, EMPTY, finalize} from "rxjs";
import { HttpClient, HttpHeaders, HttpParams, HttpEvent } from "@angular/common/http";
import {HttpErrorHandlerService} from "./http-error-handler.service";
import {LocalStorageService} from "./local-storage.service";

@Injectable({
  providedIn: 'root'
})
export class ServiceClientService {
  private defaultHeaders = new HttpHeaders({
    'Content-Type': 'application/json',
    'Platform': 'web'
  });

  constructor(private http: HttpClient, private httpErrorHandlerService: HttpErrorHandlerService,
              private localStorageService: LocalStorageService) { }

  /**
   * Centralized error handler that suppresses the "null where stream expected" error.
   * This error occurs when ErrorInterceptor returns EMPTY for 403/400/500 errors,
   * but some edge cases might still propagate. This catch ensures components never see it.
   */
  private handleHttpError(err: any): Observable<never> {
    const handledError = this.httpErrorHandlerService.handleError(err);

    // Suppress technical RxJS error caused by interceptor returning EMPTY/null
    // ("You provided 'null' where a stream was expected")
    // The ErrorInterceptor already showed a user-facing toast for 403/400/500 errors
    // Extract error message from HttpErrorResponse structure
    const errorMessage =
      (handledError as any)?.errorMessage ||
      (handledError as any)?.error?.message ||
      (handledError as any)?.error?.errorMessage ||
      handledError?.message ||
      (typeof handledError === 'string' ? handledError : String(handledError));

    if (
      typeof errorMessage === 'string' &&
      errorMessage.includes("You provided 'null' where a stream was expected")
    ) {
      return EMPTY;
    }

    // Re-throw so subscribers receive the error in their `error` callback
    throw handledError;
  }

  get<TResponse>(endPoint: string, params?: HttpParams, headerParamName?: string, headerValue?: string): Observable<TResponse> {
    const headers = this.withHeader(this.createHeader(), headerParamName, headerValue);

    return this.http.get<TResponse>(endPoint, { headers: headers, params: params }).pipe(
      map((resp: any) => resp),
      catchError((err) => this.handleHttpError(err)),
    );
  }

  post<TRequest, TResponse>(endPoint: string, request: TRequest, headerParamName?, headerValue?, callback?: () => void): Observable<TResponse> {
    const headers = headerValue ? this.defaultHeaders.set(headerParamName, headerValue) : this.defaultHeaders;
    // @ts-ignore
    return this.http.post<TRequest, TResponse>(endPoint, request, { headers: headers }).pipe(
      map((resp) => resp),
      catchError((err) => this.handleHttpError(err)),
      finalize(() => {
        if (callback) {
          callback();
        }
      }),
    );
  }

  put<TRequest, TResponse>(endPoint: string, request: TRequest): Observable<TResponse> {
    const headers = this.createHeader();
    // @ts-ignore
    return this.http.put<TRequest, TResponse>(endPoint, request, { headers: headers }).pipe(
      map((resp) => resp),
      catchError((err) => this.handleHttpError(err)),
    );
  }

  /**
   * Posts a FormData payload (file upload). Unlike `post`, this deliberately does NOT send
   * `defaultHeaders`: those set `Content-Type: application/json`, which would stop the browser
   * writing the `multipart/form-data; boundary=...` header the upload needs. Optional headers
   * (e.g. `agency`, `companyId`, `agencyId`) scope the upload on the backend — see IOHB-1764.
   */
  postImage<TRequest, TResponse>(endPoint: string, request: TRequest, headers?: Record<string, string>): Observable<TResponse> {
    const options = headers && Object.keys(headers).length ? { headers: new HttpHeaders(headers) } : {};
    // @ts-ignore
    return this.http.post<TRequest, TResponse>(endPoint, request, options).pipe(
      map((resp) => resp),
      catchError((err) => this.handleHttpError(err)),
    );
  }

  postImageWithProgress<TResponse>(endPoint: string, request: any, headers?: Record<string, string>): Observable<HttpEvent<TResponse>> {
    return this.http.post<TResponse>(endPoint, request, {
      ...(headers && Object.keys(headers).length ? { headers: new HttpHeaders(headers) } : {}),
      reportProgress: true,
      observe: 'events'
    }).pipe(
      catchError((err) => this.handleHttpError(err)),
    );
  }

  delete<TResponse>(endPoint: string, headerParamName?: string, headerValue?: string): Observable<TResponse> {
    const headers = this.withHeader(this.createHeader(), headerParamName, headerValue);
    return this.http.delete<TResponse>(endPoint, { headers: headers }).pipe(
      map((resp: any) => resp),
      catchError((err) => this.handleHttpError(err)),
    );
  }

  patch<TRequest, TResponse>(endPoint: string, request: TRequest, headerParamName?: string, headerValue?: string): Observable<TResponse> {
    const headers = this.withHeader(this.createHeader(), headerParamName, headerValue);
    return this.http.patch<TResponse>(endPoint, request, { headers: headers }).pipe(
      map((resp: any) => resp),
      catchError((err) => this.handleHttpError(err)),
    );
  }

  deleteWithBody<TRequest, TResponse>(endPoint: string, body: TRequest): Observable<TResponse> {
    const headers = this.createHeader();
    return this.http.delete<TResponse>(endPoint, { headers, body }).pipe(
      map((resp: any) => resp),
      catchError((err) => this.handleHttpError(err)),
    );
  }

  /** Adds one header when both its name and value are given, as `post` already does. */
  private withHeader(headers: HttpHeaders, name?: string, value?: string): HttpHeaders {
    return name && value ? headers.set(name, value) : headers;
  }

  createHeader() {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'Platform': 'web',
    });
  }
}
